<template>
  <div>
    <FilterBar>
      <slot name="filterbar"/>
      <IndustryFilter v-model="selectedIndustries" :industries="industries" subheader="" @update:model-value="refreshData" show-badge/>
      <FilterButton :title="ownershipFilterTitle" :active="ownershipFilterTitle !== 'Ownership Type'" data-test-activator="ownership-filter">
        <template #menu="{ close }">
          <div class="menu">
            <div class="fields">
              <div>
                <h4>Ownership Type</h4>
                <div class="wrapper">
                  <div class="field"><label><input type="checkbox" v-model="appliedOwnershipType.showPublic" data-test="show-public"/><span>Public</span></label></div>
                  <div class="field"><label><input type="checkbox" v-model="appliedOwnershipType.showPrivate" data-test="show-private"/><span>Private</span></label></div>
                </div>
              </div>
            </div>
            <div class="buttons">
              <v-btn size="small" variant="outlined" @click="appliedOwnershipType.showPublic=false;appliedOwnershipType.showPrivate=false" data-test="filter-ownership-type-clear">Clear</v-btn>
              <v-btn size="small" variant="flat" color="primary" @click="refreshData(); close()" data-test="filter-ownership-type-apply">Apply</v-btn>
            </div>
          </div>
        </template>
      </FilterButton>
      <FilterSlider title="Program Limit" description="Filter programs by total limit." prefix="$" @update:model-value="refreshData" hide-slider data-test-prefix="limit"
                    v-model="appliedLimitRange" :values="[]" :static-min="0" :value-formatter-fn="filters.compactNumber" :step="100000"/>
    </FilterBar>
    <div class="inner">
      <div class="content-grid">
        <div>
          <h2 class="mt-2">Limit By Industry</h2>
          <v-skeleton-loader type="table-tbody" v-if="loading"/>
          <table v-if="!loading" v-tablesort class="apptable sortable dense mt-4">
            <thead>
            <tr>
              <td>Top Level Industry</td>
              <td class="number sorted">Count of Programs</td>
              <td class="money">Median Limit</td>
              <td class="money">Average Limit</td>
              <td class="money">Max Limit</td>
            </tr>
            </thead>
            <tbody>
            <tr v-for="result in benchmarkingResults" :key="result.genericIndustry">
              <td :title="result.genericIndustry">{{ filters.truncate(result.genericIndustry, 50) }}</td>
              <td class="number">{{ result.countOfPrograms ?? '--' }}</td>
              <td class="money">{{ numToMoneyString(result.medianLimit, 0) ?? '--' }}</td>
              <td class="money">{{ numToMoneyString(result.averageLimit, 0) ?? '--' }}</td>
              <td class="money">{{ numToMoneyString(result.maxLimit, 0) ?? '--' }}</td>
            </tr>
            </tbody>
          </table>
        </div>
        <div>
          <fusioncharts type="column2d" width="100%" height="500" dataFormat="json" :dataSource="chartData" data-test="chart"/>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { umbrellaBenchmarkingService } from '@/services/api';
import { compactMoney, numToMoneyString } from '@/utilities/Money';
import { UmbrellaCarrierBenchmark } from '@/types/Benchmark';
import { LabelValue } from '@/types/label-value';
import FilterBar from '@/components/FilterBar.vue';
import FilterButton from '@/components/FilterButton.vue';
import IndustryFilter from '@/components/IndustryFilter.vue';
import { FilterType } from '@/types/components/FilterType';
import FilterSlider from '@/components/FilterSlider.vue';
import { filters } from '@/utilities/Filters';

const histogramChart = (labelStep: number): unknown => ({
  animation: false,
  caption: 'Total Limit Distribution',
  captionFontSize: 21,
  decimals: '0',
  xAxisName: 'Total Limit',
  yAxisName: 'Count of Programs',
  plotToolText: 'Limit: $label, Count: $value',
  showvalues: true,
  slantLabel: true,
  rotateLabels: true,
  labelStep,
  theme: 'cacs',
  exportFilename: 'CACS Umbrella/Excess Total Limits Histogram'
});

export default {
  name: 'UmbrellaBenchmarking',
  components: { FilterSlider, FilterBar, IndustryFilter, FilterButton },
  data: () => ({
    filters,
    loading: true,
    filtersDirty: false,
    benchmarkingResults: [] as UmbrellaCarrierBenchmark[],
    histogramResults: [] as LabelValue[],
    industries: undefined as FilterType<string>[] | undefined,
    appliedIndustries: [] as string[],
    appliedOwnershipType: {
      showPrivate: false,
      showPublic: false,
    },
    appliedLimitRange: [] as number[],
    selectedIndustries: [] as string[],
  }),
  methods: {
    async refreshData(): Promise<void> {
      this.loading = true;
      const ownership = [this.appliedOwnershipType.showPublic ? 'Public' : '', this.appliedOwnershipType.showPrivate ? 'Private' : ''].filter(o => o !== '');

      const [benchmarkResults, histogramResults] = await Promise.all([
        await umbrellaBenchmarkingService.LimitsByCarrier(this.selectedIndustries, ownership, this.appliedLimitRange),
        await umbrellaBenchmarkingService.TotalLimits(this.selectedIndustries, ownership, this.appliedLimitRange)
      ]);

      this.benchmarkingResults = (benchmarkResults || []).sort((a, b) => b.countOfPrograms - a.countOfPrograms);
      if (this.industries === undefined) {
        this.industries = this.benchmarkingResults.map(r => ({ text: r.genericIndustry, value: r.genericIndustry })).sort((a, b) => a.text.localeCompare(b.text));
      }
      this.histogramResults = (histogramResults || []).map(x => {
        return { label: `${compactMoney(x.limitValue)} - ${compactMoney(x.limitValue + 10_000_000)} `, value: x.countOfResults, showValue: x.countOfResults > 0 };
      });
      this.loading = false;
    },
    numToMoneyString,
    compactMoney,
  },
  computed: {
    breadcrumbs() {
      return [
        { text: 'Home', exact: true, to: '/accounts' },
        { text: 'P&C Umbrella Benchmarking', disabled: true },
      ];
    },
    chartData(): { chart: unknown, data: LabelValue[] } {
      return { chart: histogramChart(this.histogramResults.length / 8), data: this.histogramResults };
    },
    ownershipFilterTitle(): string {
      const title = [this.appliedOwnershipType.showPublic ? 'Public' : '', this.appliedOwnershipType.showPrivate ? 'Private' : ''];
      const str: string = title.filter(t => t !== '').join('; ');
      return str ? str : 'Ownership Type';
    }
  },
  created() {
    this.refreshData();
  },
};
</script>

<style scoped>
.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(800px, 1fr));
  column-gap: 2rem;
}

.wrapper {
  margin: 0 1em;
}
</style>
